import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { motion, useReducedMotion, type Transition } from 'framer-motion';
import { cn } from '@/utils/cn';
import { storyResourcesEntrance } from '@/constants/storyResourcesEntrance';
import styles from './Banner.module.scss';
import type { BannerProps } from './Banner.types';

// Coreografía de entrada del contenido (misma curva que el resto de bloques de
// la app). Los textos entran en cascada; el CTA cierra con un rebote (spring).
const REVEAL_EASE = storyResourcesEntrance.ease;
const REVEAL_Y = 12;
const REVEAL_TEXT_DURATION = 0.38;
const REVEAL_TITLE_DELAY = 0;
const REVEAL_TEXT_DELAY = 0.12;
const REVEAL_CTA_DELAY = 0.3;
// Rebote del CTA: pop de escala 0 → 1.2 → 1 en 400ms. `times` reparte el
// keyframe (60% subiendo al pico, 40% asentando). Requiere que el CTA NO tenga
// una `transition` CSS sobre `transform` (pelearía con framer y mataría el pico).
const CTA_POP_SCALE = [0, 1.2, 1];
const CTA_POP: Transition = {
  duration: 0.4,
  times: [0, 0.6, 1],
  ease: 'easeOut',
  delay: REVEAL_CTA_DELAY,
};

/**
 * Banner reutilizable y configurable: con o sin imagen de fondo, título + texto
 * opcional, y CTA (flecha) opcional. Se renderiza como `Link` (si hay `to`),
 * `button` (si hay `onClick`) o `div` informativo (si no hay acción).
 */
export function Banner({
  title,
  text,
  to,
  onClick,
  backgroundImage,
  tone = 'lavender',
  showCta,
  ctaIcon: CtaIcon = ArrowRight,
  ctaPosition,
  imagePosition = 'right center',
  className,
  'aria-label': ariaLabel,
  reveal,
}: BannerProps) {
  const reducedMotion = useReducedMotion();
  const interactive = Boolean(to || onClick);
  const withCta = showCta ?? interactive;
  const hasImage = Boolean(backgroundImage);
  const resolvedCtaPosition = ctaPosition ?? (hasImage ? 'bottom-right' : 'center');

  // Solo animamos si el padre pide coreografía (`reveal` definido) y no hay
  // reduced-motion. `shown` = estado final visible.
  const animated = reveal !== undefined && !reducedMotion;
  const shown = !animated || reveal === true;

  const rootClassName = cn(
    styles.banner,
    styles[`banner--${tone}`],
    interactive && styles['banner--interactive'],
    hasImage && styles['banner--image'],
    withCta && resolvedCtaPosition === 'bottom-right' && styles['banner--cta-bottom-right'],
    className
  );

  const style = hasImage
    ? { backgroundImage: `url(${backgroundImage})`, backgroundPosition: imagePosition }
    : undefined;

  const textTransition = (delay: number): Transition =>
    animated ? { duration: REVEAL_TEXT_DURATION, ease: REVEAL_EASE, delay } : { duration: 0 };

  const inner = (
    <>
      <div className={styles.banner__content}>
        <motion.h2
          className={styles.banner__title}
          initial={animated ? { opacity: 0, y: REVEAL_Y } : false}
          animate={{ opacity: shown ? 1 : 0, y: shown ? 0 : REVEAL_Y }}
          transition={textTransition(REVEAL_TITLE_DELAY)}
        >
          {title}
        </motion.h2>
        {text && (
          <motion.p
            className={styles.banner__text}
            initial={animated ? { opacity: 0, y: REVEAL_Y } : false}
            animate={{ opacity: shown ? 1 : 0, y: shown ? 0 : REVEAL_Y }}
            transition={textTransition(REVEAL_TEXT_DELAY)}
          >
            {text}
          </motion.p>
        )}
      </div>
      {withCta && (
        // Wrapper exterior: pop de entrada (lo anima Framer, sin transición CSS
        // de transform). Solo animamos escala; a scale 0 ya es invisible, así que
        // no hace falta opacidad (evita el "borde negro" del círculo oscuro).
        <motion.span
          className={styles.banner__ctaReveal}
          aria-hidden
          initial={animated ? { scale: 0 } : false}
          animate={{ scale: shown ? (animated ? CTA_POP_SCALE : 1) : 0 }}
          transition={animated ? CTA_POP : { duration: 0 }}
        >
          {/* Círculo interno: su transform (efecto de pulsación) lo gestiona CSS. */}
          <span className={styles.banner__cta}>
            <CtaIcon size={20} />
          </span>
        </motion.span>
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={rootClassName} style={style} aria-label={ariaLabel}>
        {inner}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={rootClassName} style={style} aria-label={ariaLabel}>
        {inner}
      </button>
    );
  }

  return (
    <div className={rootClassName} style={style}>
      {inner}
    </div>
  );
}

export default Banner;
